import type { AssistantScheduleWeekday } from './assistantSchedule';
import { normalizeVoiceText, removeVoicePhrase } from './matchVoiceSearchCity';

/**
 * Spoken “open / closed on &lt;weekday&gt;” follow-ups
 * (“שפתוח בשבת” / “שסגור בשבת” / “open on Saturday” / “closed on Saturday”).
 * Must run before {@link parseAssistantWhen} so “בשבת” is not only a calendar date,
 * and must strip פתוח/סגור so they never become venue keywords.
 */
type WeekdayPhraseEntry = { day: AssistantScheduleWeekday; phrases: string[] };

const OPEN_WEEKDAY_PHRASES: WeekdayPhraseEntry[] = [
  {
    day: 'saturday',
    phrases: [
      'שפתוח בשבת',
      'שפתוח ביום שבת',
      'פתוח בשבת',
      'פתוח ביום שבת',
      'שפתוח בימי שבת',
      'פתוח בימי שבת',
      'open on saturdays',
      'open on saturday',
      'open saturdays',
      'open saturday',
      "that's open on saturday",
      'that is open on saturday',
    ],
  },
  {
    day: 'friday',
    phrases: [
      'שפתוח בשישי',
      'שפתוח ביום שישי',
      'פתוח בשישי',
      'פתוח ביום שישי',
      'שפתוח בימי שישי',
      'פתוח בימי שישי',
      'open on fridays',
      'open on friday',
      'open fridays',
      'open friday',
    ],
  },
  {
    day: 'sunday',
    phrases: [
      'שפתוח בראשון',
      'שפתוח ביום ראשון',
      'פתוח בראשון',
      'פתוח ביום ראשון',
      'open on sundays',
      'open on sunday',
      'open sundays',
      'open sunday',
    ],
  },
  {
    day: 'monday',
    phrases: [
      'שפתוח בשני',
      'שפתוח ביום שני',
      'פתוח בשני',
      'פתוח ביום שני',
      'open on mondays',
      'open on monday',
      'open mondays',
      'open monday',
    ],
  },
  {
    day: 'tuesday',
    phrases: [
      'שפתוח בשלישי',
      'שפתוח ביום שלישי',
      'פתוח בשלישי',
      'פתוח ביום שלישי',
      'open on tuesdays',
      'open on tuesday',
      'open tuesdays',
      'open tuesday',
    ],
  },
  {
    day: 'wednesday',
    phrases: [
      'שפתוח ברביעי',
      'שפתוח ביום רביעי',
      'פתוח ברביעי',
      'פתוח ביום רביעי',
      'open on wednesdays',
      'open on wednesday',
      'open wednesdays',
      'open wednesday',
    ],
  },
  {
    day: 'thursday',
    phrases: [
      'שפתוח בחמישי',
      'שפתוח ביום חמישי',
      'פתוח בחמישי',
      'פתוח ביום חמישי',
      'open on thursdays',
      'open on thursday',
      'open thursdays',
      'open thursday',
    ],
  },
];

const CLOSED_WEEKDAY_PHRASES: WeekdayPhraseEntry[] = [
  {
    day: 'saturday',
    phrases: [
      'שסגור בשבת',
      'שסגור ביום שבת',
      'סגור בשבת',
      'סגור ביום שבת',
      'שסגור בימי שבת',
      'סגור בימי שבת',
      'closed on saturdays',
      'closed on saturday',
      'closed saturdays',
      'closed saturday',
      "that's closed on saturday",
      'that is closed on saturday',
      'closed over the weekend',
      'closed on the weekend',
      'סגור בסוף השבוע',
      'שסגור בסוף השבוע',
    ],
  },
  {
    day: 'friday',
    phrases: [
      'שסגור בשישי',
      'שסגור ביום שישי',
      'סגור בשישי',
      'סגור ביום שישי',
      'שסגור בימי שישי',
      'סגור בימי שישי',
      'closed on fridays',
      'closed on friday',
      'closed fridays',
      'closed friday',
    ],
  },
  {
    day: 'sunday',
    phrases: [
      'שסגור בראשון',
      'שסגור ביום ראשון',
      'סגור בראשון',
      'סגור ביום ראשון',
      'closed on sundays',
      'closed on sunday',
      'closed sundays',
      'closed sunday',
    ],
  },
  {
    day: 'monday',
    phrases: [
      'שסגור בשני',
      'שסגור ביום שני',
      'סגור בשני',
      'סגור ביום שני',
      'closed on mondays',
      'closed on monday',
      'closed mondays',
      'closed monday',
    ],
  },
  {
    day: 'tuesday',
    phrases: [
      'שסגור בשלישי',
      'שסגור ביום שלישי',
      'סגור בשלישי',
      'סגור ביום שלישי',
      'closed on tuesdays',
      'closed on tuesday',
      'closed tuesdays',
      'closed tuesday',
    ],
  },
  {
    day: 'wednesday',
    phrases: [
      'שסגור ברביעי',
      'שסגור ביום רביעי',
      'סגור ברביעי',
      'סגור ביום רביעי',
      'closed on wednesdays',
      'closed on wednesday',
      'closed wednesdays',
      'closed wednesday',
    ],
  },
  {
    day: 'thursday',
    phrases: [
      'שסגור בחמישי',
      'שסגור ביום חמישי',
      'סגור בחמישי',
      'סגור ביום חמישי',
      'closed on thursdays',
      'closed on thursday',
      'closed thursdays',
      'closed thursday',
    ],
  },
];

function sortPhrasesLongestFirst(entries: WeekdayPhraseEntry[]): void {
  for (const entry of entries) {
    entry.phrases.sort((a, b) => b.length - a.length);
  }
}

sortPhrasesLongestFirst(OPEN_WEEKDAY_PHRASES);
sortPhrasesLongestFirst(CLOSED_WEEKDAY_PHRASES);

export type AssistantOpenWeekdayParse = {
  /** Days that must be open (not closed). */
  weekdays: AssistantScheduleWeekday[];
  /** Days that must be closed. */
  closedWeekdays: AssistantScheduleWeekday[];
  remainder: string;
};

function consumeWeekdayPhrases(
  remainderIn: string,
  entries: WeekdayPhraseEntry[],
): { days: AssistantScheduleWeekday[]; remainder: string } {
  let remainder = remainderIn;
  const days: AssistantScheduleWeekday[] = [];
  const seen = new Set<AssistantScheduleWeekday>();
  for (const entry of entries) {
    for (const phrase of entry.phrases) {
      const needle = normalizeVoiceText(phrase);
      if (!needle || !remainder.includes(needle)) {
        continue;
      }
      remainder = removeVoicePhrase(remainder, needle);
      if (!seen.has(entry.day)) {
        seen.add(entry.day);
        days.push(entry.day);
      }
      break;
    }
  }
  return { days, remainder };
}

/** Strip open/closed-on-weekday phrases; return days that must be open or closed. */
export function parseAssistantOpenWeekday(text: string): AssistantOpenWeekdayParse {
  let remainder = normalizeVoiceText(text);
  if (!remainder) {
    return { weekdays: [], closedWeekdays: [], remainder: '' };
  }
  // Prefer closed phrases first so “סגור …” never matches open leftovers.
  const closedHit = consumeWeekdayPhrases(remainder, CLOSED_WEEKDAY_PHRASES);
  remainder = closedHit.remainder;
  const openHit = consumeWeekdayPhrases(remainder, OPEN_WEEKDAY_PHRASES);
  remainder = openHit.remainder;
  const closedSet = new Set(closedHit.days);
  const weekdays = openHit.days.filter((day) => !closedSet.has(day));
  return { weekdays, closedWeekdays: closedHit.days, remainder };
}
